import { IndicatorType } from '@melodic-ui/types';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { isPast } from 'date-fns';
import { isLessonEditable } from './lesson-helper.util';

export type LessonStatusType = 'completed' | 'absent' | 'reschedule' | 'awaiting-payment' | 'missing-attendance' | 'scheduled';

/**
 * Status variant used for dot/sliver rendering in timelines.
 * Derived from lesson status + attendance state.
 *
 * - warning: action-needed statuses (pending reschedule, awaiting payment)
 * - attention: missing-attendance error (instructor-only)
 */
export type LessonItemStatus = 'completed' | 'absent' | 'warning' | 'attention' | 'default';

const ATTENTION_STATUS_IDS = [3, 4]; // Pending Reschedule, Awaiting Payment

/**
 * Single source of truth for deriving a lesson's visual status variant.
 * Used by both the enrollment timeline (dots) and lesson-detail timeline (slivers).
 *
 * Awaiting-attendance attention is only surfaced for instructors — learners
 * have no action to take on missing attendance, so it is not shown to them. Nor is it shown on a
 * lesson from another instructor's era (predecessor or substitute), which the viewer can't record.
 */
export function getLessonItemStatus(lesson: ILessonListItem, view: 'learner' | 'instructor' = 'instructor'): LessonItemStatus {
	const awaitingAttendance =
		view === 'instructor' &&
		isLessonEditable(lesson) &&
		!ATTENTION_STATUS_IDS.includes(lesson.lessonStatusID) &&
		isPast(lesson.lessonDate) &&
		!lesson.attendanceStatusID;

	if (ATTENTION_STATUS_IDS.includes(lesson.lessonStatusID)) return 'warning';
	if (awaitingAttendance) return 'attention';
	if (lesson.attendanceStatusID === 2) return 'absent';
	if (lesson.lessonStatusID === 5) return 'completed';
	return 'default';
}

export interface LessonStatusConfig {
	icon: string;
	label: string;
	colorClass: string;
	/** Maps to md-content-box / md-tag IndicatorType for consistent coloring. */
	indicatorType: IndicatorType;
}

export const LESSON_STATUS_CONFIG: Record<LessonStatusType, LessonStatusConfig> = {
	'completed': {
		icon: 'untitled-ui/general/check-square-broken',
		label: 'Completed',
		colorClass: 'status-completed',
		indicatorType: 'default'
	},
	'absent': {
		icon: 'untitled-ui/general/slash-circle-01',
		label: 'Absent',
		colorClass: 'status-absent',
		indicatorType: 'success'
	},
	'reschedule': {
		icon: 'untitled-ui/time/clock-refresh',
		label: 'Pending Reschedule',
		colorClass: 'status-reschedule',
		indicatorType: 'warning'
	},
	'awaiting-payment': {
		icon: 'untitled-ui/time/hourglass-01',
		label: 'Awaiting Payment',
		colorClass: 'status-awaiting-payment',
		indicatorType: 'warning'
	},
	'missing-attendance': {
		icon: 'untitled-ui/alerts/alert-square',
		label: 'Awaiting Attendance',
		colorClass: 'status-missing',
		indicatorType: 'error'
	},
	'scheduled': {
		icon: 'untitled-ui/time/calendar',
		label: 'Scheduled',
		colorClass: 'status-scheduled',
		indicatorType: 'default'
	}
};

/**
 * Resolves a lesson to its full status config (icon + colors), reusing getLessonItemStatus so the
 * visual variant and the icon never drift. Single source of truth for status-driven dot icons.
 */
export function getLessonStatusConfig(lesson: ILessonListItem, view: 'learner' | 'instructor' = 'instructor'): LessonStatusConfig {
	switch (getLessonItemStatus(lesson, view)) {
		case 'completed':
			return LESSON_STATUS_CONFIG['completed'];
		case 'absent':
			return LESSON_STATUS_CONFIG['absent'];
		case 'attention':
			return LESSON_STATUS_CONFIG['missing-attendance'];
		case 'warning':
			return lesson.lessonStatusID === 4 ? LESSON_STATUS_CONFIG['awaiting-payment'] : LESSON_STATUS_CONFIG['reschedule'];
		default:
			return LESSON_STATUS_CONFIG['scheduled'];
	}
}
